import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Plus } from "lucide-react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import {
  ClientRankLevelRule,
  ClientAccessScope,
  ClientRankLevelRulesData,
  RankLevel,
  RankLevelsData,
  UpdateClientRankLevelRuleFields,
} from "../api/contracts";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  ActionsColumnHeader,
  ContentSection,
  PortalDataTable,
  PortalTableColumn,
} from "./DataDisplay";
import { ErrorState, LoadingState } from "./FeedbackStates";
import { RowActionMenu } from "./HomeworkControls";

type EditorMode =
  | { kind: "create" }
  | { kind: "edit"; rule: ClientRankLevelRule };

interface RankRuleFormErrors {
  rankLevelId?: string;
  thresholdPoints?: string;
  form?: string;
}

function sortRankLevels(rankLevels: RankLevel[]): RankLevel[] {
  return [...rankLevels].sort(
    (left, right) =>
      left.sortOrder - right.sortOrder || left.name.localeCompare(right.name),
  );
}

function sortRules(rules: ClientRankLevelRule[]): ClientRankLevelRule[] {
  return [...rules].sort(
    (left, right) =>
      left.thresholdPoints - right.thresholdPoints ||
      left.rankLevelName.localeCompare(right.rankLevelName),
  );
}

function mutationErrors(error: unknown): RankRuleFormErrors {
  if (!(error instanceof ClinicianPortalApiError)) {
    return { form: "The Rank Rule could not be saved. Please try again." };
  }
  switch (error.code) {
    case "DUPLICATE_CLIENT_RANK_LEVEL_RULE":
      return {
        rankLevelId:
          "This client already has a rule for that Rank Level. Reactivate the archived rule instead.",
      };
    case "INVALID_RANK_LEVEL_ID":
    case "RANK_LEVEL_NOT_FOUND":
    case "RANK_LEVEL_DISABLED":
      return { rankLevelId: "Select an available Rank Level." };
    case "INVALID_THRESHOLD_POINTS":
      return {
        thresholdPoints: "Threshold Points must be a non-negative integer.",
      };
    default:
      return { form: "The Rank Rule could not be saved. Please try again." };
  }
}

function RankRuleEditor({
  clinicianCode,
  patientId,
  mode,
  rankLevels,
  rules,
  onCancel,
  onSaved,
}: {
  clinicianCode: string;
  patientId: string;
  mode: EditorMode;
  rankLevels: RankLevel[];
  rules: ClientRankLevelRule[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const mutation = usePortalMutation();
  const submittingRef = useRef(false);
  const currentRule = mode.kind === "edit" ? mode.rule : undefined;
  const usedByOtherRule = useMemo(
    () =>
      new Set(
        rules
          .filter((rule) => rule.id !== currentRule?.id)
          .map((rule) => rule.rankLevelId),
      ),
    [currentRule?.id, rules],
  );
  const options = useMemo(() => {
    const available = rankLevels.filter(
      (rankLevel) =>
        (rankLevel.enabled && !usedByOtherRule.has(rankLevel.id)) ||
        rankLevel.id === currentRule?.rankLevelId,
    );
    if (
      currentRule &&
      !available.some((rankLevel) => rankLevel.id === currentRule.rankLevelId)
    ) {
      available.push({
        id: currentRule.rankLevelId,
        key: currentRule.rankLevelKey,
        name: currentRule.rankLevelName,
        sortOrder: Number.MAX_SAFE_INTEGER,
        enabled: false,
      });
    }
    return sortRankLevels(available);
  }, [currentRule, rankLevels, usedByOtherRule]);
  const [rankLevelId, setRankLevelId] = useState(
    currentRule?.rankLevelId ?? options[0]?.id ?? "",
  );
  const [thresholdPoints, setThresholdPoints] = useState(
    currentRule ? String(currentRule.thresholdPoints) : "",
  );
  const [validationErrors, setValidationErrors] = useState<RankRuleFormErrors>(
    {},
  );
  const isSubmitting = mutation.state.status === "pending";
  const apiErrors =
    mutation.state.status === "error"
      ? mutationErrors(mutation.state.error)
      : {};
  const errors = { ...validationErrors, ...apiErrors };
  const heading = mode.kind === "create" ? "Add Rank Rule" : "Edit Rank Rule";
  const headingId = React.useId();
  const rankLevelInputId = React.useId();
  const thresholdInputId = React.useId();
  const rankLevelErrorId = React.useId();
  const thresholdErrorId = React.useId();

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || isSubmitting) return;

    const parsedThreshold = Number(thresholdPoints);
    const nextErrors: RankRuleFormErrors = {};
    if (!rankLevelId) nextErrors.rankLevelId = "Rank Level is required.";
    if (
      thresholdPoints.trim() === "" ||
      !Number.isInteger(parsedThreshold) ||
      parsedThreshold < 0
    ) {
      nextErrors.thresholdPoints =
        "Threshold Points must be a non-negative integer.";
    }
    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    if (
      currentRule &&
      rankLevelId === currentRule.rankLevelId &&
      parsedThreshold === currentRule.thresholdPoints
    ) {
      onCancel();
      return;
    }

    submittingRef.current = true;
    try {
      const result = await mutation.run(
        mode.kind === "create" ? "create" : `edit:${mode.rule.id}`,
        (signal) => {
          if (mode.kind === "create") {
            return clinicianPortalApi.createClientRankLevelRule(
              clinicianCode,
              patientId,
              { rankLevelId, thresholdPoints: parsedThreshold },
              signal,
            );
          }

          const fields: UpdateClientRankLevelRuleFields = {};
          if (rankLevelId !== mode.rule.rankLevelId) {
            fields.rankLevelId = rankLevelId;
          }
          if (parsedThreshold !== mode.rule.thresholdPoints) {
            fields.thresholdPoints = parsedThreshold;
          }
          return clinicianPortalApi.updateClientRankLevelRule(
            clinicianCode,
            patientId,
            mode.rule.id,
            fields,
            signal,
          );
        },
      );
      if (result) onSaved();
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <section className="cp-client-value-editor" aria-labelledby={headingId}>
      <div className="cp-client-value-editor__header">
        <h3 id={headingId}>{heading}</h3>
        <p>Set a client-specific point threshold for a Rank Level.</p>
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <div className="cp-form-field">
          <label htmlFor={rankLevelInputId}>
            Rank Level <span aria-hidden="true">*</span>
          </label>
          <select
            id={rankLevelInputId}
            className="cp-form-input"
            value={rankLevelId}
            required
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.rankLevelId)}
            aria-describedby={errors.rankLevelId ? rankLevelErrorId : undefined}
            onChange={(event) => {
              clearErrors();
              setRankLevelId(event.target.value);
            }}
          >
            <option value="" disabled>
              Select a Rank Level
            </option>
            {options.map((rankLevel) => (
              <option key={rankLevel.id} value={rankLevel.id}>
                {rankLevel.name}
              </option>
            ))}
          </select>
          {errors.rankLevelId ? (
            <p className="cp-form-error" id={rankLevelErrorId}>
              {errors.rankLevelId}
            </p>
          ) : null}
        </div>
        <div className="cp-form-field">
          <label htmlFor={thresholdInputId}>
            Threshold Points <span aria-hidden="true">*</span>
          </label>
          <input
            id={thresholdInputId}
            className="cp-form-input cp-form-input--no-spinner"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={thresholdPoints}
            required
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.thresholdPoints)}
            aria-describedby={
              errors.thresholdPoints ? thresholdErrorId : undefined
            }
            onChange={(event) => {
              clearErrors();
              setThresholdPoints(event.target.value);
            }}
          />
          {errors.thresholdPoints ? (
            <p className="cp-form-error" id={thresholdErrorId}>
              {errors.thresholdPoints}
            </p>
          ) : null}
        </div>
        {errors.form ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {errors.form}
          </p>
        ) : null}
        <div className="cp-form-actions">
          <button
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? mode.kind === "create"
                ? "Adding…"
                : "Saving…"
              : mode.kind === "create"
                ? "Add Rank Rule"
                : "Save"}
          </button>
        </div>
      </form>
    </section>
  );
}

function ArchiveRankRuleDialog({
  rule,
  isArchiving,
  hasError,
  onCancel,
  onConfirm,
}: {
  rule: ClientRankLevelRule | null;
  isArchiving: boolean;
  hasError: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => {
    if (rule) cancelButtonRef.current?.focus();
  }, [rule]);

  if (!rule) return null;

  return (
    <div
      className="cp-confirm-dialog__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isArchiving) onCancel();
      }}
    >
      <div
        className="cp-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !isArchiving) {
            event.preventDefault();
            onCancel();
          }
        }}
      >
        <h2 id={titleId}>Archive this Rank Rule?</h2>
        <p id={descriptionId}>
          This rule will stop participating in the backend Badge Level
          calculation, but it will remain available for historical records.
        </p>
        {hasError ? (
          <p className="cp-confirm-dialog__error" role="alert">
            The Rank Rule could not be archived. Please try again.
          </p>
        ) : null}
        <div className="cp-confirm-dialog__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isArchiving}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cp-button cp-button--danger"
            disabled={isArchiving}
            onClick={onConfirm}
          >
            {isArchiving ? "Archiving…" : "Archive Rank Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ClientRankLevelRulesSection({
  clinicianCode,
  patientId,
  onRulesMutated,
  readOnly = false,
  accessScope = "assigned",
}: {
  clinicianCode: string;
  patientId: string;
  onRulesMutated: () => void;
  readOnly?: boolean;
  accessScope?: ClientAccessScope;
}) {
  const rankLevelsRequest = useCallback(
    (signal: AbortSignal) =>
      accessScope === "team"
        ? clinicianPortalApi.getRankLevels(clinicianCode, signal, "team")
        : clinicianPortalApi.getRankLevels(clinicianCode, signal),
    [accessScope, clinicianCode],
  );
  const rulesRequest = useCallback(
    (signal: AbortSignal) =>
      accessScope === "team"
        ? clinicianPortalApi.getClientRankLevelRules(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getClientRankLevelRules(
            clinicianCode,
            patientId,
            signal,
          ),
    [accessScope, clinicianCode, patientId],
  );
  const rankLevelsState = usePortalData(rankLevelsRequest);
  const rulesState = usePortalData(rulesRequest);
  const mutation = usePortalMutation();
  const [cachedRankLevelsData, setCachedRankLevelsData] =
    useState<RankLevelsData>();
  const [cachedRulesData, setCachedRulesData] =
    useState<ClientRankLevelRulesData>();
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] =
    useState<ClientRankLevelRule | null>(null);

  const latestRankLevelsData =
    rankLevelsState.status === "success" ||
    rankLevelsState.status === "refreshing"
      ? rankLevelsState.data
      : undefined;
  const latestRulesData =
    rulesState.status === "success" || rulesState.status === "refreshing"
      ? rulesState.data
      : undefined;

  useEffect(() => {
    if (latestRankLevelsData) setCachedRankLevelsData(latestRankLevelsData);
  }, [latestRankLevelsData]);
  useEffect(() => {
    if (latestRulesData) setCachedRulesData(latestRulesData);
  }, [latestRulesData]);

  const rankLevelsData = latestRankLevelsData ?? cachedRankLevelsData;
  const rulesData = latestRulesData ?? cachedRulesData;
  const available = Boolean(
    rankLevelsData?.found &&
      rankLevelsData.authorized &&
      rankLevelsData.therapist &&
      rankLevelsData.patient === null &&
      rulesData?.found &&
      rulesData.authorized &&
      rulesData.therapist &&
      rulesData.patient,
  );
  const rules = rulesData?.rules ?? [];
  const activeRules = sortRules(rules.filter((rule) => rule.enabled));
  const archivedRules = sortRules(rules.filter((rule) => !rule.enabled));
  const enabledRankLevels = sortRankLevels(
    (rankLevelsData?.rankLevels ?? []).filter((rankLevel) => rankLevel.enabled),
  );
  const usedRankLevelIds = new Set(rules.map((rule) => rule.rankLevelId));
  const availableForCreate = enabledRankLevels.filter(
    (rankLevel) => !usedRankLevelIds.has(rankLevel.id),
  );
  const isMutating = mutation.state.status === "pending";
  const initialLoading =
    (!rankLevelsData && rankLevelsState.status === "loading") ||
    (!rulesData && rulesState.status === "loading");
  const initialError =
    (!rankLevelsData && rankLevelsState.status === "error") ||
    (!rulesData && rulesState.status === "error");

  const refreshAll = () => {
    rankLevelsState.refresh();
    rulesState.refresh();
  };

  const handleMutationSuccess = () => {
    setEditor(null);
    setArchiveTarget(null);
    mutation.reset();
    rulesState.refresh();
    onRulesMutated();
  };

  const setEnabled = async (rule: ClientRankLevelRule, enabled: boolean) => {
    if (readOnly || isMutating) return;
    mutation.reset();
    const action = `${enabled ? "reactivate" : "archive"}:${rule.id}`;
    const result = await mutation.run(action, (signal) =>
      clinicianPortalApi.setClientRankLevelRuleEnabled(
        clinicianCode,
        patientId,
        rule.id,
        enabled,
        signal,
      ),
    );
    if (result) handleMutationSuccess();
  };

  const activeRuleColumns: readonly PortalTableColumn<ClientRankLevelRule>[] = [
    {
      key: "actions",
      header: <ActionsColumnHeader />,
      headerLabel: "Actions",
      initialWidth: 60,
      minWidth: 60,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (rule) => (
        <RowActionMenu
          clinicianCode={clinicianCode}
          activityName={`${rule.rankLevelName} Rank Rule`}
          canEdit={!isMutating && !editor}
          onEdit={() => {
            mutation.reset();
            setEditor({ kind: "edit", rule });
          }}
          showDuplicate={false}
          canDelete={!isMutating && !editor}
          onDelete={() => {
            mutation.reset();
            setArchiveTarget(rule);
          }}
          deleteLabel="Archive"
          deletingLabel="Archiving…"
        />
      ),
    },
    {
      key: "rankLevel",
      header: "Rank Levels",
      initialWidth: 300,
      minWidth: 180,
      render: (rule) => (
        <span className="cp-homework-name">{rule.rankLevelName}</span>
      ),
    },
    {
      key: "pointsRequired",
      header: "Points Required",
      initialWidth: 180,
      minWidth: 140,
      render: (rule) => rule.thresholdPoints.toLocaleString(),
    },
  ];

  const displayedColumns = readOnly ? activeRuleColumns.slice(1) : activeRuleColumns;

  return (
    <ContentSection
      title="Rank Level Rules"
      actions={
        available && !readOnly ? (
          <button
            type="button"
            className="cp-button cp-button--primary"
            disabled={
              isMutating || Boolean(editor) || availableForCreate.length === 0
            }
            onClick={() => {
              mutation.reset();
              setEditor({ kind: "create" });
            }}
          >
            <Plus aria-hidden="true" />
            Add Rank Rule
          </button>
        ) : null
      }
    >
      <p className="cp-rank-rules__helper">
        Set the point thresholds this client must reach for each rank level.
      </p>
      {initialLoading ? (
        <LoadingState label="Loading Rank Level Rules" />
      ) : null}
      {initialError ? (
        <ErrorState
          error={
            rankLevelsState.status === "error"
              ? rankLevelsState.error
              : rulesState.status === "error"
                ? rulesState.error
                : new Error("Rank Level Rules are unavailable")
          }
          onRetry={refreshAll}
        />
      ) : null}
      {rankLevelsData && rulesData && !available ? (
        <ErrorState error={new Error("Rank Level Rules are not available")} />
      ) : null}
      {available ? (
        <div className="cp-client-values cp-rank-rules">
          {(rankLevelsState.status === "error" ||
            rulesState.status === "error") &&
          !initialError ? (
            <div
              className="cp-inline-notice cp-inline-notice--error"
              role="alert"
            >
              <div>
                <strong>Rank Level Rules could not be refreshed.</strong>
                <span>The last loaded rules remain visible.</span>
              </div>
              <button type="button" onClick={refreshAll}>
                Try again
              </button>
            </div>
          ) : null}

          {editor && !readOnly ? (
            <RankRuleEditor
              key={editor.kind === "edit" ? editor.rule.id : "create"}
              clinicianCode={clinicianCode}
              patientId={patientId}
              mode={editor}
              rankLevels={rankLevelsData?.rankLevels ?? []}
              rules={rules}
              onCancel={() => setEditor(null)}
              onSaved={handleMutationSuccess}
            />
          ) : null}

          {availableForCreate.length === 0 ? (
            <p className="cp-rank-rules__availability">
              {enabledRankLevels.length === 0
                ? "No enabled Rank Levels are available."
                : "All available Rank Levels already have rules. Reactivate an archived rule instead of creating a duplicate."}
            </p>
          ) : null}

          {activeRules.length ? (
            <PortalDataTable
              columns={displayedColumns}
              records={activeRules}
              getRecordKey={(rule) => rule.id}
              caption="Active Rank Level Rules"
            />
          ) : (
            <div className="cp-client-values__empty">
              <p>No active Rank Level Rules.</p>
              <small>
                Badge Level falls back to Beginner when no enabled rule
                qualifies.
              </small>
            </div>
          )}

          {archivedRules.length ? (
            <div className="cp-client-values__archived">
              <button
                type="button"
                className="cp-client-values__archive-toggle"
                aria-expanded={showArchived}
                onClick={() => setShowArchived((current) => !current)}
              >
                {showArchived ? "Hide" : "Show"} archived (
                {archivedRules.length})
              </button>
              {showArchived ? (
                <div
                  className="cp-client-value-list"
                  aria-label="Archived Rank Level Rules"
                >
                  {archivedRules.map((rule) => {
                    const action = `reactivate:${rule.id}`;
                    const isReactivating =
                      mutation.state.status === "pending" &&
                      mutation.state.action === action;
                    const hasError =
                      mutation.state.status === "error" &&
                      mutation.state.action === action;
                    return (
                      <article
                        className="cp-client-value-card cp-client-value-card--archived"
                        key={rule.id}
                      >
                        <div className="cp-client-value-card__content">
                          <div className="cp-client-value-card__title">
                            <h3>{rule.rankLevelName}</h3>
                            <span>Archived</span>
                          </div>
                          <p className="cp-rank-rule__threshold">
                            {rule.thresholdPoints} points
                          </p>
                          {hasError ? (
                            <p
                              className="cp-client-value-card__error"
                              role="alert"
                            >
                              The Rank Rule could not be reactivated. Please try
                              again.
                            </p>
                          ) : null}
                        </div>
                        {!readOnly ? <div className="cp-client-value-card__actions">
                          <button
                            type="button"
                            className="cp-button cp-button--secondary"
                            aria-label={`Reactivate ${rule.rankLevelName} Rank Rule`}
                            disabled={isMutating || Boolean(editor)}
                            onClick={() => setEnabled(rule, true)}
                          >
                            {isReactivating ? "Reactivating…" : "Reactivate"}
                          </button>
                        </div> : null}
                      </article>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {!readOnly ? <ArchiveRankRuleDialog
        rule={archiveTarget}
        isArchiving={Boolean(
          archiveTarget &&
            mutation.state.status === "pending" &&
            mutation.state.action === `archive:${archiveTarget.id}`,
        )}
        hasError={Boolean(
          archiveTarget &&
            mutation.state.status === "error" &&
            mutation.state.action === `archive:${archiveTarget.id}`,
        )}
        onCancel={() => {
          setArchiveTarget(null);
          mutation.reset();
        }}
        onConfirm={() => {
          if (archiveTarget) setEnabled(archiveTarget, false);
        }}
      /> : null}
    </ContentSection>
  );
}
